import { DateTime } from 'luxon';
import { mask } from 'superstruct';

import { AddGoogleAdsAdTemplateDto } from './google-ads-ad.dto';
import { AddGoogleAdsCampaignTemplateDto } from './google-ads-campaign.dto';
import {
	AddGoogleAdsGroupTemplateDto,
	googleAdsChannelsOptions,
} from './google-ads-group.dto';
import { AddGoogleAdsTemplateDto } from './google-ads-template.dto';
import type { AddKeywordDto } from './keyword.dto';
import type { AddPromotionAssetDto } from './promotion-asset.dto';

export function isDemandGenCampaign(
	campaignType: string | null | undefined
): boolean {
	return campaignType === 'DEMAND_GEN';
}

export function isSearchCampaign(
	campaignType: string | null | undefined
): boolean {
	return campaignType === 'SEARCH';
}

export function isDisplayCampaign(
	campaignType: string | null | undefined
): boolean {
	return campaignType === 'DISPLAY';
}

export function isDemandGenOrSearchCampaign(
	campaignType: string | null | undefined
): boolean {
	return isDemandGenCampaign(campaignType) || isSearchCampaign(campaignType);
}

export function supportsBudgetType(
	campaignType: string | null | undefined
): boolean {
	return isDemandGenOrSearchCampaign(campaignType);
}

export const DefaultGoogleAdsTemplateValuesDto = () =>
	mask(
		{
			name: 'SE :: Promo :: Template name',
			archived: false,
		} satisfies AddGoogleAdsTemplateDto,
		AddGoogleAdsTemplateDto
	);

export const CreateGoogleAdsTemplateValuesDto = () =>
	({
		name: 'SE :: Promo :: Template name',
		archived: false,
	}) as AddGoogleAdsTemplateDto;

export const DefaultGoogleAdsCampaignTemplateValuesDto = () =>
	mask(
		{
			name: 'SE :: Promo :: Campaign name',
			type: 'SEARCH',
			status: 'ENABLED',
			language: 'EN',
			biddingStrategyType: 'MAXIMIZE_CONVERSIONS',
			startDate: DateTime.utc().toISO(),
			endDate: DateTime.utc().plus({ days: 1 }).toISO(),
			includeLocations: false,
			budget: 0,
			budgetType: 'DAILY_BUDGET',
			campaignGoal: 'CLICKS',
			conversionGoals: {},
			locationTargeting: 'PRESENCE',
			useMerchantCenter: false,
			images: [],
			calloutAssets: Array.from({ length: 10 }).fill('') as string[],
			rsaCampaignHeadlines: Array.from({ length: 3 }).fill('') as string[],
			rsaCampaignDescriptions: Array.from({ length: 2 }).fill('') as string[],
			enableAiMax: false,
			aiMaxTextAssetAutomation: false,
			aiMaxFinalUrlExpansionTextAssetAutomation: false,
			aiMaxTermExclusions: [],
			aiMaxMessagingRestrictions: [],
		} satisfies AddGoogleAdsCampaignTemplateDto,
		AddGoogleAdsCampaignTemplateDto
	);

export const CreateGoogleAdsCampaignTemplateValuesDto = () =>
	({
		name: 'SE :: Promo :: Campaign name',
		type: 'SEARCH',
		status: 'ENABLED',
		language: 'EN',
		biddingStrategyType: 'MAXIMIZE_CONVERSIONS',
		startDate: DateTime.utc().toISO(),
		endDate: DateTime.utc().plus({ days: 1 }).toISO(),
		includeLocations: false,
		budget: 0,
		budgetType: 'DAILY_BUDGET',
		campaignGoal: 'CLICKS',
		conversionGoals: {},
		locationTargeting: 'PRESENCE',
		useMerchantCenter: false,
		images: [],
		calloutAssets: Array.from({ length: 10 }).fill('') as string[],
		rsaCampaignHeadlines: Array.from({ length: 3 }).fill('') as string[],
		rsaCampaignDescriptions: Array.from({ length: 2 }).fill('') as string[],
		enableAiMax: false,
		aiMaxTextAssetAutomation: false,
		aiMaxFinalUrlExpansionTextAssetAutomation: false,
		aiMaxTermExclusions: [],
		aiMaxMessagingRestrictions: [],
	}) as AddGoogleAdsCampaignTemplateDto;

export const DefaultGoogleAdsGroupTemplateValuesDto = () =>
	mask(
		{
			name: 'SE :: Promo :: Group name',
			status: 'ENABLED',
			maxCPC: 1,
			label: '',

			topics: [],
			audiences: [],
			userLists: [],
			keywords: [],
			images: [],
			channels: googleAdsChannelsOptions,
			googleDisplayNetwork: false,
			calloutAssets: Array.from({ length: 10 }).fill('') as string[],
			aiMaxDisableSearchTermMatching: false,
		} satisfies AddGoogleAdsGroupTemplateDto,
		AddGoogleAdsGroupTemplateDto
	);

export const CreateGoogleAdsGroupTemplateValuesDto = () =>
	({
		name: 'SE :: Promo :: Group name',
		status: 'ENABLED',
		maxCPC: 1,
		label: '',

		topics: [],
		audiences: [],
		userLists: [],
		keywords: [],
		images: [],
		channels: googleAdsChannelsOptions,
		googleDisplayNetwork: false,
		calloutAssets: Array.from({ length: 10 }).fill('') as string[],
		aiMaxDisableSearchTermMatching: false,
	}) as AddGoogleAdsGroupTemplateDto;

export const DefaultGoogleAdsAdTemplateValuesDto = () =>
	mask(
		{
			name: 'SE :: Promo :: Ad name',
			type: 'RESPONSIVE_SEARCH_AD',
			status: 'ENABLED',
			finalUrl: '/',
			label: '',
			images: [],
			adObject: DefaultGoogleAdsAdObjectSearchValuesDto(),
		} satisfies AddGoogleAdsAdTemplateDto,
		AddGoogleAdsAdTemplateDto
	);

export const CreateGoogleAdsAdTemplateValuesDto = (campaignType?: string) => {
	let defaultAdType = 'RESPONSIVE_SEARCH_AD';
	let defaultAdObject: unknown = DefaultGoogleAdsAdObjectSearchValuesDto();

	if (isDemandGenCampaign(campaignType)) {
		defaultAdType = 'DEMAND_GEN_IMAGE';
		defaultAdObject = DefaultGoogleAdsAdObjectDemandGenImageValuesDto();
	} else if (isDisplayCampaign(campaignType)) {
		defaultAdType = 'RESPONSIVE_DISPLAY_AD';
		defaultAdObject = DefaultGoogleAdsAdObjectDisplayValuesDto();
	}

	return {
		name: 'SE :: Promo :: Ad name',
		type: defaultAdType,
		status: 'ENABLED',
		finalUrl: '',
		label: '',
		images: [],
		adObject: defaultAdObject,
	} as AddGoogleAdsAdTemplateDto;
};

export const DefaultGoogleAdsAdObjectDisplayValuesDto = () => ({
	headline: Array.from({ length: 5 }).fill('') as string[],
	longHeadline: Array.from({ length: 1 }).fill('') as string[],
	description: Array.from({ length: 5 }).fill('') as string[],
});

export const DefaultGoogleAdsAdObjectSearchValuesDto = () => ({
	headline: Array.from({ length: 15 }).fill('') as string[],
	description: Array.from({ length: 4 }).fill('') as string[],
	displayUrlPath: Array.from({ length: 2 }).fill('') as string[],
});

export const DefaultGoogleAdsAdObjectDemandGenImageValuesDto = () => ({
	headline: Array.from({ length: 5 }).fill('') as string[],
	description: Array.from({ length: 5 }).fill('') as string[],
	finalUrl: '',
	cta: 'UNSPECIFIED',
});

export const DefaultGoogleAdsAdObjectDemandGenVideoValuesDto = () => ({
	headline: Array.from({ length: 5 }).fill('') as string[],
	longHeadline: Array.from({ length: 5 }).fill('') as string[],
	description: Array.from({ length: 5 }).fill('') as string[],
	finalUrl: '',
	videos: Array.from({ length: 5 }).fill('') as string[],
	cta: 'UNSPECIFIED',
});

export const DefaultGoogleAdsAdObjectDemandGenCarouselValuesDto = () => ({
	cards: [],
	headline: Array.from({ length: 1 }).fill('') as string[],
	description: Array.from({ length: 1 }).fill('') as string[],
});

export const DefaultGoogleAdsAdObjectDemandGenProductValuesDto = () => ({
	headline: Array.from({ length: 1 }).fill('') as string[],
	description: Array.from({ length: 1 }).fill('') as string[],
	finalUrl: '',
	cta: 'UNSPECIFIED',
});

export const DefaultKeywordValuesDto = () =>
	({
		name: '',
		type: 'standard',
		list: [],
	}) as AddKeywordDto;

export const DefaultPromotionAssetValuesDto = () =>
	({
		name: '',
		occasion: 'UNSPECIFIED',
		language: 'en',
		currency: 'USD',
		promoType: 'PERCENT_OFF',
		promoValue: 0,
		item: '',
		finalUrl: '',
		details: 'NONE',
		ordersOverValue: undefined,
		promoCode: '',
		barcode: '',
		barcodeType: '',
		qrCode: '',
		startDate: '',
		endDate: '',
		promotionStartDate: '',
		promotionEndDate: '',
	}) as AddPromotionAssetDto;
